import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { PageHeader, Pagination } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './ViewProductCategory.module.scss';
import DataTable from '../../components/DataTable/DataTable';
import EditProductCategoryModal from '../../components/Modals/EditProductCategoryModal';
import { searchProductCategories } from '../../api/Products';
import { toast } from 'react-toastify';
import Search from '../../components/Search/Search.component';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import { useGetQueryParams } from '../../hooks/useGetQueryParams';
import { useSetQueryParams } from '../../hooks/useSetQueryParams';
import { APP_PARAMS } from '../../constants/appParams';

const ViewProductCategory = (props) => {
    const queryParams = useGetQueryParams([
        APP_PARAMS.PAGE,
        APP_PARAMS.SEARCH_KEY,
        'status',
    ]);
    const setQueryParam = useSetQueryParams();

    const [state, setState] = useComponentState({
        tableHeading: {
            column: [
                'Category Name',
                'Display Order',
                'Status',
                'Banner Image',
                'Display Title',
                'Sub Title',
            ],
        },
        categories: [],
        loading: false,
        notFoundMessage: '',
        fetchError: '',
        expanded: {},
        currentPage: 0,
        pageSize: 10,
        totalPages: 0,
        showEditModal: false,
        categoryToEdit: null,
        showDeleteModal: false,
        categoryToDelete: null,
        searchKey: queryParams.searchKey || '',
        statusFilter: queryParams.status || null,
        isClearable: false,
        statusOptions: [
            { key: null, label: 'All Statuses' },
            { key: 'PUBLISHED', label: 'Published' },
            { key: 'UNPUBLISHED', label: 'Unpublished' },
        ],
    });

    const {
        categories,
        tableHeading,
        loading,
        fetchError,
        expanded,
        currentPage,
        totalPages,
        showEditModal,
        categoryToEdit,
        searchKey,
        statusFilter,
        statusOptions,
        pageSize,
    } = state;

    useEffect(() => {
        const page = Number(queryParams.page || 1) - 1;
        const search = queryParams.searchKey || '';
        const status = queryParams.status || null;

        fetchCategories(page, pageSize, search, status);
        // eslint-disable-next-line
    }, [
        queryParams.page,
        queryParams.searchKey,
        queryParams.status,
    ]);


    const fetchCategories = async (page, pageSize, search = '', status = null) => {
        setState({
            loading: true,
            categories: [],
            fetchError: '',
            notFoundMessage: '',
        });

        try {
            let response;
            response = await searchProductCategories(
                props.auth.auth.token,
                searchKey || '',
                'name',
                page,
                pageSize,
                status ? status : null,
            );

            if (response.list && response.list.length > 0) {
                setState({
                    categories: response.list,
                    totalPages: response.totalPageCount,
                    currentPage: response.pageNo,
                });
            } else {
                toast.error('No product categories found');
            }
        } catch (error) {
            toast.error('Failed to load product categories');
        }
        setState({ loading: false });
    };

    const handleCategoriesPagination = (selectedPage) => {
        fetchCategories(selectedPage, pageSize, searchKey, statusFilter);
    };

    const handleStatusChange = (key) => {
        setQueryParam({
            [APP_PARAMS.PAGE]: 1,
            'status': key,
            [APP_PARAMS.SEARCH_KEY]: searchKey || queryParams.searchKey || null,
        });
        setState({
            statusFilter: key,
        });
    };

    const handleSearch = () => {
        if (!searchKey && !queryParams.searchKey) return;

        const finalSearchKey = searchKey || queryParams.searchKey;

        setQueryParam({
            [APP_PARAMS.SEARCH_KEY]: finalSearchKey,
            [APP_PARAMS.PAGE]: 1,
            status: statusFilter,
        });

        setState({
            searchKey: finalSearchKey,
            isClearable: true,
        });
    };

    const handleClearSearch = () => {
        setQueryParam({
            [APP_PARAMS.SEARCH_KEY]: null,
            [APP_PARAMS.PAGE]: 1,
            status: statusFilter,
        });

        setState({
            searchKey: '',
            isClearable: false,
        });
    };


    const searchOnChange = (value) => {
        setState({
            searchKey: value,
            isClearable: false,
        });
    };

    const renderFilterByStatus = () => {
        return (
            <DropdownButton
                id='status-dropdown'
                title={
                    statusOptions.find((opt) => opt.key === statusFilter)?.label ||
                    'All Statuses'
                }
                variant='primary'
                className={styles.filterButton}
            >
                {statusOptions.map((option) => (
                    <Dropdown.Item
                        key={option.key}
                        onClick={() => handleStatusChange(option.key)}
                        active={statusFilter === option.key}
                    >
                        {option.label}
                    </Dropdown.Item>
                ))}
            </DropdownButton>
        );
    };

    // Render image cell for banner images
    const renderCategoryImageCell = (imageUrl) => {
        if (!imageUrl) return <span>No image</span>;
        return (
            <img
                src={imageUrl}
                alt='Banner preview'
                style={{
                    width: '200px',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                }}
            />
        );
    };

    // Handlers for edit modal
    const handleEditCategory = (category) => {
        setState({
            showEditModal: true,
            categoryToEdit: category,
        });
    };

    const handleEditModalClose = () => {
        setState({
            showEditModal: false,
            categoryToEdit: null,
        });
    };

    const handleEditSuccess = () => {
        fetchCategories(currentPage, pageSize, searchKey, statusFilter);
        handleEditModalClose();
    };

    // Delete handlers
    const handleDeleteCategory = (category) => {
        setState({
            showDeleteModal: true,
            categoryToDelete: category,
        });
    };

    // Truncate text and provide expand/collapse option
    const renderExpandableText = (text, categoryId, field, length = 80) => {
        if (!text) return '';
        const key = `${categoryId}_${field}`;
        const isExpanded = expanded[key];

        if (isExpanded) {
            return (
                <span>
                    {text}{' '}
                    <button
                        type="button"
                        onClick={() => {
                            setState({
                                expanded: {
                                    ...expanded,
                                    [key]: false,
                                },
                            });
                        }}
                        className={styles.readMoreLess}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'blue',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                        }}
                    >
                        ...Read Less
                    </button>
                </span>
            );
        }

        return text.length > length ? (
            <span title={text}>
                {text.substring(0, length)}...{' '}
                <button
                    type="button"
                    onClick={() => {
                        setState({
                            expanded: {
                                ...expanded,
                                [key]: true,
                            },
                        });
                    }}
                    className={styles.readMoreLess}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'blue',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                    }}
                >
                    Read More
                </button>
            </span>
        ) : (
            text
        );
    };

    return (
        <div className={styles.root_div}>
            <PageHeader text='Product Categories' />

            {/* Search Bar and Filter in Same Row */}
            <div className={styles.filter_block_status}>
                <div className={`d-flex ${styles.filter_row}`}>
                    {renderFilterByStatus()}
                    <div className={styles.search_area} style={{ flex: 1 }}>
                        <Search
                            placeholder={'Enter Category Name'}
                            onBtnPress={handleSearch}
                            onClearPress={handleClearSearch}
                            onChange={searchOnChange}
                            max={50}
                            handleValueFromParent
                            value={searchKey}
                            searchKey={queryParams.searchKey}
                        />
                    </div>
                </div>
            </div>

            {/* Data table for product categories */}
            <div className={styles.categoryTableWrapper}>
                <DataTable
                    tableData={categories}
                    tableheading={tableHeading}
                    tag='product-categories'
                    renderCategoryImageCell={renderCategoryImageCell}
                    renderExpandableText={renderExpandableText}
                    expanded={expanded}
                    fetchError={fetchError}
                    search_filter_loading={loading}
                    handleEditCategory={handleEditCategory}
                    handleDeleteCategory={handleDeleteCategory}
                />
            </div>


            {/* Pagination controls */}
            {!loading && !fetchError && (
                <Pagination
                    pageCount={totalPages}
                    onChange={handleCategoriesPagination}
                    currentPage={currentPage}
                />
            )}

            {/* Edit product category modal */}
            <EditProductCategoryModal
                show={showEditModal}
                onHide={handleEditModalClose}
                category={categoryToEdit}
                token={props.auth.auth.token}
                onSuccess={handleEditSuccess}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth,
    };
};

export default connect(mapStateToProps)(ViewProductCategory);